<script>
import { useAlert } from 'dashboard/composables';
import { mapActions } from 'vuex';
import { FIELD_CLASS } from '../constants';
import { apiErrorMessage } from '../../../../api/realEstate/errors';
import ImageUpload from '../components/ImageUpload.vue';

// Gallery images are upload-only (multipart `file`, see
// api/realEstate/media.js) — there's no "paste a URL" add form any more.
// Each row also dispatches its own action right away (caption change,
// reorder, remove) rather than collecting edits for a single Save, since
// every write here re-fetches the full project detail anyway.
export default {
  name: 'GalleryModal',
  components: { ImageUpload },
  props: {
    project: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      fieldClass: FIELD_CLASS,
    };
  },
  computed: {
    gallery() {
      return this.project.gallery || [];
    },
  },
  methods: {
    ...mapActions('realEstate', [
      'addGalleryImageFile',
      'setGalleryCaption',
      'reorderGalleryImages',
      'removeGalleryImage',
    ]),
    async handleAdd(file) {
      try {
        await this.addGalleryImageFile({
          propertyId: this.project.property_id,
          file,
          caption: '',
        });
        useAlert(this.$t('REAL_ESTATE.GALLERY.SUCCESS_ADDED'));
      } catch (err) {
        useAlert(apiErrorMessage(this, err, 'REAL_ESTATE.GALLERY.ERROR_ADD'));
        // eslint-disable-next-line no-console
        console.error(err);
      }
    },
    async handleCaptionChange(imageId, caption) {
      try {
        await this.setGalleryCaption({
          propertyId: this.project.property_id,
          imageId,
          caption,
        });
        useAlert(this.$t('REAL_ESTATE.GALLERY.SUCCESS_CAPTION'));
      } catch (err) {
        useAlert(
          apiErrorMessage(this, err, 'REAL_ESTATE.GALLERY.ERROR_CAPTION')
        );
        // eslint-disable-next-line no-console
        console.error(err);
      }
    },
    async handleMove(index, direction) {
      const target = index + direction;
      if (target < 0 || target >= this.gallery.length) return;
      const reordered = [...this.gallery];
      [reordered[index], reordered[target]] = [
        reordered[target],
        reordered[index],
      ];
      try {
        await this.reorderGalleryImages({
          propertyId: this.project.property_id,
          imageIds: reordered.map(image => image.image_id),
        });
        useAlert(this.$t('REAL_ESTATE.GALLERY.SUCCESS_REORDERED'));
      } catch (err) {
        useAlert(
          apiErrorMessage(this, err, 'REAL_ESTATE.GALLERY.ERROR_REORDER')
        );
        // eslint-disable-next-line no-console
        console.error(err);
      }
    },
    async handleRemove(imageId) {
      try {
        await this.removeGalleryImage({
          propertyId: this.project.property_id,
          imageId,
        });
        useAlert(this.$t('REAL_ESTATE.GALLERY.SUCCESS_REMOVED'));
      } catch (err) {
        useAlert(
          apiErrorMessage(this, err, 'REAL_ESTATE.GALLERY.ERROR_REMOVE')
        );
        // eslint-disable-next-line no-console
        console.error(err);
      }
    },
  },
};
</script>

<template>
  <div
    class="fixed inset-0 flex items-center justify-center bg-slate-800/80 z-50"
  >
    <div
      class="bg-white dark:bg-slate-900 rounded-lg p-6 shadow-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto"
    >
      <div class="flex items-center mb-4 justify-between">
        <h1 class="text-xl font-bold text-slate-900 dark:text-slate-100">
          {{ $t('REAL_ESTATE.GALLERY.MODAL_TITLE') }}
        </h1>
        <woot-button
          variant="clear"
          color-scheme="alert"
          icon="dismiss"
          @click="$emit('close')"
        />
      </div>

      <div class="space-y-2 mb-4">
        <div
          v-for="(image, index) in gallery"
          :key="image.image_id"
          class="flex items-center gap-3 border border-slate-200 dark:border-slate-700 rounded-lg p-2"
        >
          <div
            class="relative w-14 h-14 shrink-0 rounded-md overflow-hidden border border-slate-200 dark:border-slate-700 bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-slate-400"
          >
            <fluent-icon icon="image" size="18" />
            <img
              :src="image.url"
              alt=""
              class="absolute inset-0 w-full h-full object-cover"
              @error="$event.target.remove()"
            />
          </div>
          <input
            :value="image.caption"
            :placeholder="$t('REAL_ESTATE.GALLERY.CAPTION_PLACEHOLDER')"
            :class="fieldClass"
            class="flex-1 !py-1.5"
            @change="
              handleCaptionChange(image.image_id, $event.target.value.trim())
            "
          />
          <div class="flex items-center gap-1 shrink-0">
            <woot-button
              variant="clear"
              size="small"
              icon="chevron-up"
              :is-disabled="index === 0"
              :title="$t('REAL_ESTATE.GALLERY.MOVE_UP')"
              @click="handleMove(index, -1)"
            />
            <woot-button
              variant="clear"
              size="small"
              icon="chevron-down"
              :is-disabled="index === gallery.length - 1"
              :title="$t('REAL_ESTATE.GALLERY.MOVE_DOWN')"
              @click="handleMove(index, 1)"
            />
            <woot-button
              variant="clear"
              size="small"
              color-scheme="alert"
              icon="delete"
              :title="$t('REAL_ESTATE.GALLERY.REMOVE')"
              @click="handleRemove(image.image_id)"
            />
          </div>
        </div>
        <p v-if="!gallery.length" class="text-sm text-slate-400">
          {{ $t('REAL_ESTATE.GALLERY.EMPTY_MODAL') }}
        </p>
      </div>

      <div class="pt-3 border-t border-slate-200 dark:border-slate-700">
        <ImageUpload
          :label="$t('REAL_ESTATE.GALLERY.ADD_BUTTON')"
          :removable="false"
          @select="handleAdd"
        />
      </div>

      <div
        class="flex justify-end gap-3 pt-4 mt-2 border-t border-slate-200 dark:border-slate-700"
      >
        <woot-button type="button" @click="$emit('close')">
          {{ $t('REAL_ESTATE.GALLERY.DONE') }}
        </woot-button>
      </div>
    </div>
  </div>
</template>
